import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { Router, RouterModule } from "@angular/router";
import { AuthService } from "../../../services/api/auth/auth.service";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";

import { LangSwitchComponent } from "../../../shared/components/lang-switch/lang-switch.component";
import { first } from "rxjs";
import { SvgIconComponent } from "../../../shared/components/svg-icon/svg-icon.component";

@Component({
  standalone: true,
  selector: "app-login",
  imports: [
    InputTextModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    RouterModule,
    ProgressSpinnerModule,
    FormsModule,
    LangSwitchComponent,
    SvgIconComponent,
  ],
  templateUrl: "./login.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./login.component.scss",
})
export class LoginComponent {
  private auth = inject(AuthService);
  private router = inject(Router);

  public loginForm: FormGroup = new FormGroup({
    username: new FormControl(null, Validators.required),
    password: new FormControl(null, Validators.required),
  });

  public loading = signal(false);
  public passwordVisible = signal(false);

  /**
   * Attempts to log in using the provided username and password.
   * If the request is successful, redirects to /dashboard/home.
   * If the request fails, displays an error message.
   */
  public login() {
    const username = this.loginForm.get("username")?.value;
    const password = this.loginForm.get("password")?.value;

    if (this.loginForm.valid && username && password) {
      this.loading.set(true);
      this.auth
        .login(username, password)
        .pipe(first())
        .subscribe({
          next: () => {
            this.router.navigate(["/dashboard/home"]);
            this.loading.set(false);
          },
          error: () => {
            this.loading.set(false);
          },
        });
    }
  }

  /**
   * Toggle the visibility of the password input field.
   */
  togglePasswordVisibility() {
    this.passwordVisible.update((value) => !value);
  }

  /**
   * Handle Enter key press event.
   * If pressed, calls `login` method.
   * @param event KeyboardEvent
   */
  onKeyUp(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.login();
    }
  }
}
